import { useEffect, useState } from 'react';
import { Keyboard, Platform } from 'react-native';
import { KeyboardEvents, useKeyboardState } from 'react-native-keyboard-controller';
import useWebKeyboardHeight from '@/hooks/useWebKeyboardHeight';

/**
 * Height from a KeyboardController event (`height`) or RN Keyboard event
 * (`endCoordinates.height`). Either source can be missing depending on OEM.
 */
export function readKeyboardEventHeight(event) {
  if (!event || typeof event !== 'object') return 0;
  const controllerHeight = Number(event.height);
  if (Number.isFinite(controllerHeight) && controllerHeight > 0) {
    return controllerHeight;
  }
  const rnHeight = Number(event.endCoordinates?.height);
  if (Number.isFinite(rnHeight) && rnHeight > 0) {
    return rnHeight;
  }
  return 0;
}

/**
 * Merge a keyboard-show event without allowing a broken zero-height event from
 * one source to erase a valid height already reported by another source.
 */
export function mergeShownKeyboardHeight(currentHeight, event) {
  const nextHeight = readKeyboardEventHeight(event);
  if (nextHeight > 0) return nextHeight;

  const current = Number(currentHeight);
  return Number.isFinite(current) && current > 0 ? current : 0;
}

/**
 * Web uses the visual viewport. Native prefers KeyboardController's IME inset
 * and keeps RN's `Keyboard` as a fallback.
 *
 * The composer is absolutely positioned at `bottom: 0`, so a height of 0 means
 * it stays under the keyboard with no visible text and no send button. Rather
 * than depend on one OEM's event path, read both and use whichever reports a
 * height: Galaxy/One UI devices hit this while Pixel does not, and we have no
 * device log proving which of the two sources goes quiet there.
 */
export function pickComposerKeyboardHeight({
  platform,
  webHeight = 0,
  controllerHeight = 0,
  fallbackHeight = 0,
}) {
  if (platform === 'web') return Math.max(0, Number(webHeight) || 0);
  const controller = Number(controllerHeight);
  const fallback = Number(fallbackHeight);
  const safeController = Number.isFinite(controller) && controller > 0 ? controller : 0;
  const safeFallback = Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
  // Where the controller reports a live IME inset, use it and ignore the RN
  // fallback so a slightly larger layout height (nav bar, toolbar) cannot
  // double-lift a composer that was already correct. The fallback only lifts
  // anything when the controller reports nothing at all.
  return safeController > 0 ? safeController : safeFallback;
}

const readCurrentHeight = () => (Platform.OS === 'ios' || Platform.OS === 'android'
  ? readKeyboardEventHeight({ endCoordinates: Keyboard.metrics() })
  : 0);
const readIOSKeyboardVisible = () => Platform.OS === 'ios' && Keyboard.isVisible();

export default function useComposerKeyboardHeight() {
  const webHeight = useWebKeyboardHeight();
  const controllerHeight = useKeyboardState((state) => state.height) || 0;
  // A new chat can mount while the keyboard is already open. On devices
  // relying on RN's fallback there may be no new show event until it reopens.
  const [fallbackHeight, setFallbackHeight] = useState(readCurrentHeight);
  const [isIOSKeyboardVisible, setIOSKeyboardVisible] = useState(readIOSKeyboardVisible);

  useEffect(() => {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return undefined;

    const apply = (event) => {
      if (Platform.OS === 'ios') setIOSKeyboardVisible(true);
      setFallbackHeight((currentHeight) => (
        mergeShownKeyboardHeight(currentHeight, event)
      ));
    };
    const clear = () => {
      if (Platform.OS === 'ios') setIOSKeyboardVisible(false);
      setFallbackHeight(0);
    };

    const subscriptions = [
      KeyboardEvents.addListener('keyboardWillShow', apply),
      KeyboardEvents.addListener('keyboardDidShow', apply),
      KeyboardEvents.addListener('keyboardDidHide', clear),
      Keyboard.addListener('keyboardDidShow', apply),
      Keyboard.addListener('keyboardDidHide', clear),
    ];

    // Close the render-to-subscription gap, including a hide during mounting.
    // Keep the lift until didHide: clearing at willHide drops fallback-only
    // composers behind the keyboard during its dismissal animation.
    setFallbackHeight(readCurrentHeight());
    setIOSKeyboardVisible(readIOSKeyboardVisible());

    return () => {
      subscriptions.forEach((subscription) => subscription.remove());
    };
  }, []);

  return pickComposerKeyboardHeight({
    platform: Platform.OS,
    webHeight,
    // A native hide can arrive before (or without) the controller's hide.
    // Its cached height must not leave an empty keyboard-sized gap on iOS.
    controllerHeight: Platform.OS === 'ios' && !isIOSKeyboardVisible ? 0 : controllerHeight,
    fallbackHeight,
  });
}
